import { useState, useEffect } from 'react';
import useStore from '@/store/useStore';
import useMediaQuery from '@/hooks/useMediaQuery';
import useNavLinksBehavior from '@/hooks/useNavLinksBehavior';
import NavLinks from '@/widgets/navLinks/NavLinks';
import type { NavPromoTile } from '@/widgets/navLinks/types';
import Bag from './components/Bag';
import Contact from './components/Contact';
import Logo from './components/Logo';
import Search from './components/Search';
import Stores from './components/Stores';
import User from './components/User';
import VirtualAppointment from './components/VirtualAppointment';
import Heart from './components/Heart';
import LocalCountries from './components/LocalCountries';
import GbleCountries from './components/GbleCountries';

const NAV_ROOT_ID = 'be-nav-links-root';

export type HeaderProps = {
  className?: string;
  navLinks?: any;
  /**
   * Nav markup pre-rendered by `renderNavHtml`. When present the nav is mounted as a static
   * island that React never reconciles, so Dynamic Yield's pre-hydration edits survive instead
   * of being reverted on hydration. Falls back to the React tree when omitted.
   */
  navHtml?: string;
};

function LoadingWrapper({ loading, children, height = '20px' }) {
  return loading ? (
    <div className={`tw-h-[${height}] tw-bg-gray-200`} />
  ) : (
    <div className="tw-float-right tw-flex tw-justify-end tw-items-center tw-h-[27px]">{children}</div>
  );
}

export default function Header({ className = '', navLinks, navHtml }: HeaderProps) {
  const { store } = useStore(state => state.showroom);
  const [isLoading, setisLoading] = useState(true);
  const { globalEFlag, globalESite, currSymbol } = useStore(state => state.user);
  const promoTopNav = useStore(state => state.builderData?.promo?.topNav) ?? null;
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);
  const { localGeToggle } = useStore(state => state.glLocation);
  const [mounted, setMounted] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    setMounted(true);
  }, []);

  useNavLinksBehavior({
    rootId: NAV_ROOT_ID,
    enabled: Boolean(navHtml),
    currencyCode: currSymbol || '$',
    isGlobalEFlag: !!globalEFlag,
    isGlobalESite: !!globalESite,
    onPromoTermsClick: () => setIsPromoModalVisible(true),
  });

  function setCountryElement() {
    // Still loading
    if (localGeToggle === null || globalEFlag === null) {
      return null;
    }

    if (localGeToggle) {
      return <LocalCountries />;
    }

    // GlobalE third party will inject CTA button.
    return <GbleCountries injectGlbeCountry={isDesktop} />;
  }

  return (
    <div className={`king-header ${className}`}>
      <div className="wrapper container1170">
        <div className="row cn-row">
          <div className="king-quick-help king-header-serves">
            <ul className="list-inline contact-us">
              {!isLoading && (
                <li className="mr-4">
                  <Contact />
                </li>
              )}
              {store?.location_name === 'Other' && !isLoading && (
                <li className="mr-4">
                  <Stores />
                </li>
              )}
              <li className="mr-4">
                <VirtualAppointment loading={isLoading} setLoading={setisLoading} />
              </li>
            </ul>
          </div>
          <div className="king-header-navgation tw-static">
            <Logo />
            {/* Match Django mount: #be-nav-links-host > #be-nav-links-root */}
            <div id="be-nav-links-host" className="king-header-navgation-menu" style={{ minHeight: 48 }}>
              {navHtml ? (
                <div id={NAV_ROOT_ID} dangerouslySetInnerHTML={{ __html: navHtml }} />
              ) : (
                <div id={NAV_ROOT_ID}>
                  <NavLinks
                    navLinks={navLinks || []}
                    promo={(promoTopNav as NavPromoTile) ?? null}
                    currencyCode={currSymbol || '$'}
                    isGlobalEFlag={!!globalEFlag}
                    isGlobalESite={!!globalESite}
                    onPromoTermsClick={() => setIsPromoModalVisible(true)}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="king-quick-help king-header-user tw-relative tw-pointer-events-none">
            <LoadingWrapper loading={!mounted} height="0px">
              {setCountryElement()}
            </LoadingWrapper>
            <ul className="list-inline site-header-personal tw-w-[300px] tw-flex tw-justify-end tw-items-center tw-absolute tw-right-[71px] tw-pointer-events-auto">
              <li>
                <Search />
              </li>
              <li className="dropdown">
                <User />
              </li>
              <li className="dropdown iconHearth">
                <Heart />
              </li>
              <li className="dropdown mr-4">
                <Bag />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
